import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon,
  BookmarkIcon,
  ShareIcon,
  CheckIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

import RichTextEditor from '../../components/diary/RichTextEditor';
import TagSelector from '../../components/forms/TagSelector';
import MoodPicker from '../../components/forms/MoodPicker';
import FolderSelector from '../../components/forms/FolderSelector';
import { diaryService } from '../../services/diary';

const CreateEntry = () => {
  const navigate = useNavigate();
  const [content, setContent] = useState('');
  const [tags, setTags] = useState([]);
  const [mood, setMood] = useState('');
  const [folder, setFolder] = useState('');
  const [isPinned, setIsPinned] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState('saved');

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch
  } = useForm({
    mode: 'onChange'
  });

  const title = watch('title', '');

  // Auto-save functionality
  React.useEffect(() => {
    const autoSave = () => {
      if (title || content) {
        setAutoSaveStatus('saving');
        // Simulate auto-save
        setTimeout(() => {
          setAutoSaveStatus('saved');
        }, 1000);
      }
    };

    const timer = setTimeout(autoSave, 2000);
    return () => clearTimeout(timer);
  }, [title, content]);

  const onSubmit = async (data) => {
    if (!content.trim()) {
      toast.error('Please add some content to your entry');
      return;
    }

    setIsSubmitting(true);
    try {
      const entryData = {
        title: data.title,
        content,
        tags,
        mood,
        folder: folder || undefined,
        isPinned
      };

      const newEntry = await diaryService.createEntry(entryData);
      toast.success('Entry created successfully!');
      navigate(`/entries/${newEntry._id}`);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create entry');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (title || content || tags.length > 0) {
      if (window.confirm('Are you sure you want to discard this entry?')) {
        navigate('/dashboard');
      }
    } else {
      navigate('/dashboard');
    }
  };

  return (
    <div className="min-h-screen bg-paper-white">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <motion.button
                onClick={handleCancel}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <ArrowLeftIcon className="h-5 w-5 text-gray-600" />
              </motion.button>
              <h1 className="text-xl font-serif font-semibold text-ink-black">
                New Entry
              </h1>
            </div>

            <div className="flex items-center space-x-4">
              {/* Auto-save status */}
              <motion.div
                className="flex items-center space-x-2 text-sm"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                {autoSaveStatus === 'saving' && (
                  <>
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-primary-600"></div>
                    <span className="text-gray-500">Saving...</span>
                  </>
                )}
                {autoSaveStatus === 'saved' && (
                  <>
                    <CheckIcon className="h-3 w-3 text-green-500" />
                    <span className="text-gray-500">Saved</span>
                  </>
                )}
              </motion.div>

              {/* Action buttons */}
              <div className="flex items-center space-x-2">
                <motion.button
                  type="button"
                  onClick={() => setIsPinned(!isPinned)}
                  className={`p-2 rounded-lg transition-colors ${
                    isPinned
                      ? 'bg-red-100 text-red-600'
                      : 'hover:bg-gray-100 text-gray-600'
                  }`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  title={isPinned ? 'Unpin entry' : 'Pin entry'}
                >
                  <BookmarkIcon className={`h-5 w-5 ${isPinned ? 'fill-current' : ''}`} />
                </motion.button>

                <motion.button
                  onClick={handleCancel}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Cancel
                </motion.button>

                <motion.button
                  onClick={handleSubmit(onSubmit)}
                  disabled={isSubmitting || !title.trim()}
                  className={`btn-primary flex items-center space-x-2 ${
                    isSubmitting || !title.trim() ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  whileHover={!isSubmitting && title.trim() ? { scale: 1.05 } : {}}
                  whileTap={!isSubmitting && title.trim() ? { scale: 0.95 } : {}}
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Publishing...</span>
                    </>
                  ) : (
                    <>
                      <span>Publish Entry</span>
                      <CheckIcon className="h-4 w-4" />
                    </>
                  )}
                </motion.button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
          {/* Title */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <input
              {...register('title', {
                required: 'Title is required',
                maxLength: {
                  value: 200,
                  message: 'Title cannot exceed 200 characters'
                }
              })}
              type="text"
              placeholder="What's on your mind today?"
              className="w-full text-3xl font-serif font-bold text-ink-black placeholder-gray-400 border-none outline-none bg-transparent resize-none"
              style={{ lineHeight: '1.2' }}
            />
            {errors.title && (
              <motion.p
                className="mt-2 text-sm text-red-600"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                {errors.title.message}
              </motion.p>
            )}
          </motion.div>

          {/* Metadata Row */}
          <motion.div
            className="grid md:grid-cols-3 gap-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <MoodPicker selectedMood={mood} onChange={setMood} />
            <FolderSelector selectedFolder={folder} onChange={setFolder} />
            <TagSelector selectedTags={tags} onChange={setTags} />
          </motion.div>

          {/* Content Editor */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <label className="block text-sm font-medium text-gray-700 mb-4">
              Content
            </label>
            <RichTextEditor
              value={content}
              onChange={setContent}
              placeholder="Start writing your thoughts, experiences, or reflections..."
            />
          </motion.div>

          {/* Entry Preview */}
          {(title || content) && (
            <motion.div
              className="card p-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <h3 className="text-lg font-semibold text-ink-black mb-4">
                Preview
              </h3>
              <div className="diary-paper p-6 rounded-lg">
                <div className="flex items-center space-x-3 mb-4">
                  {mood && <span className="text-2xl">{mood}</span>}
                  <h4 className="text-xl font-serif font-semibold text-ink-black">
                    {title || 'Untitled Entry'}
                  </h4>
                  {isPinned && (
                    <BookmarkIcon className="h-5 w-5 text-red-500 fill-current" />
                  )}
                </div>
                
                {content && (
                  <div
                    className="prose prose-lg max-w-none text-gray-700"
                    dangerouslySetInnerHTML={{ __html: content }}
                  />
                )}
                
                <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
                  <div className="flex items-center space-x-4 text-sm text-gray-500">
                    <span>{new Date().toLocaleDateString()}</span>
                    {folder && (
                      <span className="bg-primary-100 text-primary-700 px-2 py-1 rounded-full">
                        {folder}
                      </span>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    {tags.slice(0, 3).map((tag) => (
                      <span
                        key={tag}
                        className="bg-gray-100 text-gray-600 px-2 py-1 rounded-full text-xs"
                      >
                        #{tag}
                      </span>
                    ))}
                    {tags.length > 3 && (
                      <span className="text-xs text-gray-500">
                        +{tags.length - 3} more
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </form>
      </div>
    </div>
  );
};

export default CreateEntry;
